<template>
  <!-- Заявка на лизинг (своя техника, позиция каталога, недвижимость, быстрый
       расчёт) / расчёт по КП поставщика — общая форма для окна и страницы
       /catalog/application. Уходит персональному менеджеру (GitLkApi /requestOwn)
       со всеми данными и файлом счёта/КП во вложении.
       Чтобы клиент не пугался объёма: сразу видно только нужное (тип, техника,
       условия, контакты), контакты из профиля — карточкой, всё необязательное
       (марка, модель, год, количество, поставщик, файл, комментарий) свёрнуто
       в список «Дополнительно» -->
  <div v-if="success" class="app-done">
    <div class="app-done__icon" aria-hidden="true">
      <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>
    </div>
    <div class="app-done__title">Заявка отправлена</div>
    <p class="app-done__text">
      Персональный менеджер свяжется с Вами в ближайшее время и подготовит
      расчёт.
    </p>
    <button
      v-if="cancelable"
      type="button"
      class="btn btn-base"
      @click="$emit('cancel')"
    >
      Готово
    </button>
    <RouterLink v-else to="/catalog" class="btn btn-base"
      >Вернуться в каталог</RouterLink
    >
  </div>

  <form v-else class="app-form" novalidate @submit.prevent="send">
    <!-- подсказка вверху: коротко, сколько это займёт и что дальше.
         Из быстрого расчёта — встроена в карточку расчёта (ниже) -->
    <div v-if="!calc" class="app-hint">
      <span class="app-hint__icon" aria-hidden="true">
        <!-- КП — документ, иначе — трубка -->
        <svg v-if="kind === 'kp'" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" /><path d="M14 3v5h5M9 13h6M9 17h4" /></svg>
        <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2" /></svg>
      </span>
      <span class="app-hint__text">
        <b>{{ hint.title }}</b>
        <small>{{ hint.text }}</small>
      </span>
    </div>

    <!-- из быстрого расчёта: одна карточка в стиле подсказки — что произошло и
         результат расчёта (справочно, уходит менеджеру в письме); стоимость,
         аванс и срок предзаполнены ниже, их можно уточнить -->
    <div v-if="calc" class="app-hint app-hint_calc">
      <span class="app-hint__icon" aria-hidden="true">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="5" y="3" width="14" height="18" rx="2" /><path d="M8 7h8M8 11h.01M12 11h.01M16 11h.01M8 15h.01M12 15h.01M16 15h.01M8 18h.01M12 18h4" /></svg>
      </span>
      <span class="app-hint__text">
        <b>Расчёт перенесён в заявку</b>
        <small>Сумма договора {{ rub(calc.total) }} · не оферта</small>
      </span>
      <span class="app-hint__pay">
        <b>{{ rub(calc.payment) }}</b>
        <small>в месяц</small>
      </span>
    </div>

    <!-- расчёт по КП: документ — главное, поэтому загрузка первой и крупно -->
    <div v-if="kind === 'kp'" class="app-form__block">
      <FilePicker
        :file="file"
        :error="errors.file"
        title="Счёт или КП поставщика"
        @pick="pickFile"
        @remove="removeFile"
      />
      <div v-if="errors.file" class="app-form__error">{{ errors.file }}</div>
    </div>

    <!-- что берут в лизинг: тип чипсами и наименование -->
    <div class="app-form__block">
      <div id="app-type-l" class="app-form__q">Что хотите взять в лизинг?</div>
      <div class="app-chips" role="radiogroup" aria-labelledby="app-type-l">
        <button
          v-for="type in types"
          :key="type.value"
          type="button"
          class="app-chip"
          :class="form.PLType === type.value && 'is-active'"
          role="radio"
          :aria-checked="form.PLType === type.value ? 'true' : 'false'"
          @click="setType(type.value)"
        >
          {{ type.label }}
        </button>
      </div>
      <div v-if="errors.PLType" class="app-form__error">{{ errors.PLType }}</div>

      <div class="app-form__field">
        <ui-input-box-floating :error="errors.PLName">
          <ui-input
            v-model="form.PLName"
            class="form-control"
            placeholder="Наименование"
          />
          <ui-input-label>{{ nameLabel }}</ui-input-label>
        </ui-input-box-floating>
        <div v-if="errors.PLName" class="app-form__error">{{ errors.PLName }}</div>
      </div>
    </div>

    <!-- условия: стоимость (валюта внутри поля), аванс и срок в одну строку -->
    <div class="app-form__block">
      <div class="app-form__q">Условия</div>
      <div class="app-terms">
        <div class="app-terms__price">
          <div class="app-money">
            <ui-input-box-floating :error="errors.PLPrice">
              <input
                :value="priceText"
                class="form-control"
                inputmode="numeric"
                placeholder="Стоимость"
                @input="onPriceInput"
              />
              <ui-input-label>{{ priceLabel }}</ui-input-label>
            </ui-input-box-floating>
            <select
              v-model="form.currency"
              class="app-money__cur"
              aria-label="Валюта"
            >
              <option
                v-for="cur in currencies"
                :key="cur.value"
                :value="cur.value"
              >
                {{ cur.sign }}
              </option>
            </select>
          </div>
          <div v-if="errors.PLPrice" class="app-form__error">{{ errors.PLPrice }}</div>
        </div>
        <div>
          <ui-input-box-floating :error="errors.advance">
            <ui-input
              v-model="form.advance"
              class="form-control"
              inputmode="numeric"
              maxlength="2"
              placeholder="Аванс"
            />
            <ui-input-label>Аванс, %</ui-input-label>
          </ui-input-box-floating>
          <div v-if="errors.advance" class="app-form__error">{{ errors.advance }}</div>
        </div>
        <div>
          <ui-input-box-floating :error="errors.term">
            <ui-input
              v-model="form.term"
              class="form-control"
              inputmode="numeric"
              maxlength="2"
              placeholder="Срок"
            />
            <ui-input-label>Срок, мес.</ui-input-label>
          </ui-input-box-floating>
          <div v-if="errors.term" class="app-form__error">{{ errors.term }}</div>
        </div>
      </div>

      <!-- одобренный лимит: для любого типа; ничего не резервирует -->
      <div
        v-if="limitValue > 0"
        class="app-limit"
        :class="limitWarning && 'app-limit_warning'"
      >
        <label class="app-limit__switch">
          <span>
            <b>Использовать одобренный лимит</b>
            <small>Доступно {{ rub(limitValue) }}</small>
          </span>
          <input v-model="form.useLimit" type="checkbox" class="app-switch" />
        </label>
        <div class="app-limit__notice" role="status">{{ limitNotice }}</div>
      </div>
    </div>

    <!-- контакты: из профиля — одной карточкой, иначе (или «Изменить») — поля -->
    <div class="app-form__block">
      <div class="app-form__q">Как с Вами связаться</div>
      <div v-if="contactCard" class="app-contact">
        <span class="app-contact__avatar" aria-hidden="true">{{ initials }}</span>
        <span class="app-contact__info">
          <b>{{ form.name }}</b>
          <small>{{ contactLine }}</small>
        </span>
        <button
          type="button"
          class="app-contact__edit"
          @click="contactsEdit = true"
        >
          Изменить
        </button>
      </div>
      <div v-else class="row g-3">
        <div class="col-sm-6">
          <ui-input-box-floating :error="errors.name">
            <ui-input v-model="form.name" class="form-control" placeholder="Имя" />
            <ui-input-label>Ваше имя</ui-input-label>
          </ui-input-box-floating>
          <div v-if="errors.name" class="app-form__error">{{ errors.name }}</div>
        </div>
        <div class="col-sm-6">
          <ui-input-box-floating :error="errors.phone">
            <ui-input
              v-model="form.phone"
              v-maska
              data-maska="+7 (###) ###-##-##"
              class="form-control"
              placeholder="Телефон"
            />
            <ui-input-label>Телефон</ui-input-label>
          </ui-input-box-floating>
          <div v-if="errors.phone" class="app-form__error">{{ errors.phone }}</div>
        </div>
        <div class="col-12">
          <ui-input-box-floating :error="errors.email">
            <ui-input v-model="form.email" class="form-control" placeholder="E-mail" />
            <ui-input-label>E-mail</ui-input-label>
          </ui-input-box-floating>
          <div v-if="errors.email" class="app-form__error">{{ errors.email }}</div>
        </div>
      </div>
    </div>

    <!-- дополнительно: всё необязательное свёрнуто списком; заполненное видно
         краткой строкой под названием пункта -->
    <div class="app-form__block">
      <div class="app-form__q">
        Дополнительно <span class="app-form__optional">необязательно</span>
      </div>
      <div class="app-extra">
        <div class="app-extra__item">
          <button
            type="button"
            class="app-extra__head"
            :aria-expanded="detailsOpen ? 'true' : 'false'"
            @click="detailsOpen = !detailsOpen"
          >
            <span class="app-extra__icon" aria-hidden="true">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 6h16M4 12h16M4 18h10" /></svg>
            </span>
            <span class="app-extra__text">
              <b>{{ detailsTitle }}</b>
              <small v-if="detailsSummary">{{ detailsSummary }}</small>
            </span>
            <svg class="app-extra__chev" :class="detailsOpen && 'is-open'" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg>
          </button>
          <div v-if="detailsOpen" class="app-extra__body">
            <div class="row g-3">
              <template v-if="form.PLType !== 'Недвижимость'">
                <div class="col-6">
                  <ui-input-box-floating>
                    <ui-input v-model="form.PLBrand" class="form-control" placeholder="Марка" />
                    <ui-input-label>Марка</ui-input-label>
                  </ui-input-box-floating>
                </div>
                <div class="col-6">
                  <ui-input-box-floating>
                    <ui-input v-model="form.PLModel" class="form-control" placeholder="Модель" />
                    <ui-input-label>Модель</ui-input-label>
                  </ui-input-box-floating>
                </div>
                <div class="col-6">
                  <ui-input-box-floating :error="errors.PLDate">
                    <ui-input
                      v-model="form.PLDate"
                      class="form-control"
                      inputmode="numeric"
                      maxlength="4"
                      placeholder="Год"
                    />
                    <ui-input-label>Год выпуска</ui-input-label>
                  </ui-input-box-floating>
                  <div v-if="errors.PLDate" class="app-form__error">{{ errors.PLDate }}</div>
                </div>
                <div class="col-6">
                  <ui-input-box-floating :error="errors.PLQty">
                    <ui-input
                      v-model="form.PLQty"
                      class="form-control"
                      inputmode="numeric"
                      placeholder="Количество"
                    />
                    <ui-input-label>Количество, шт.</ui-input-label>
                  </ui-input-box-floating>
                  <div v-if="errors.PLQty" class="app-form__error">{{ errors.PLQty }}</div>
                </div>
              </template>
              <div class="col-12">
                <ui-input-box-floating>
                  <ui-input
                    v-model="form.supplier"
                    class="form-control"
                    placeholder="Поставщик"
                  />
                  <ui-input-label>Поставщик — название или ИНН</ui-input-label>
                </ui-input-box-floating>
              </div>
            </div>
          </div>
        </div>

        <div v-if="kind !== 'kp'" class="app-extra__item">
          <FilePicker
            slim
            :file="file"
            :error="errors.file"
            @pick="pickFile"
            @remove="removeFile"
          />
        </div>

        <div class="app-extra__item">
          <button
            type="button"
            class="app-extra__head"
            :aria-expanded="commentOpen ? 'true' : 'false'"
            @click="commentOpen = !commentOpen"
          >
            <span class="app-extra__icon" aria-hidden="true">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12a8 8 0 0 1-11.6 7.1L4 20.5l1.4-4.8A8 8 0 1 1 21 12z" /></svg>
            </span>
            <span class="app-extra__text">
              <b>Комментарий для менеджера</b>
              <small v-if="!commentOpen && form.comment.trim()">{{ form.comment }}</small>
            </span>
            <svg class="app-extra__chev" :class="commentOpen && 'is-open'" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg>
          </button>
          <div v-if="commentOpen" class="app-extra__body">
            <ui-input-box-floating>
              <ui-textarea
                v-model="form.comment"
                class="form-control"
                placeholder="Комментарий"
                style="height: 88px"
              />
              <ui-input-label>Удобное время звонка, детали</ui-input-label>
            </ui-input-box-floating>
          </div>
        </div>
      </div>
      <div v-if="kind !== 'kp' && errors.file" class="app-form__error">
        {{ errors.file }}
      </div>
    </div>

    <!-- одна большая кнопка, закреплена внизу; под ней — снятие тревоги -->
    <div class="app-form__footer">
      <div v-if="error" class="app-form__footer-error" role="alert">{{ error }}</div>
      <ui-button
        type="submit"
        class="btn-base app-form__submit"
        :loading="sending"
        :disabled="sending"
        >Отправить заявку</ui-button
      >
      <p class="app-form__trust">
        Заявка ни к чему не обязывает — менеджер перезвонит и уточнит детали
      </p>
    </div>
  </form>
</template>

<script>
import { mapGetters } from "vuex";
import { postApi, uploadApi } from "@/run";
import FilePicker from "./FilePicker.vue";

// типы предмета лизинга — как в GitLkApi requestOwn; лимит доступен для любого типа
// value — как в справочнике сервера, label — короткая подпись чипса
const TYPES = [
  { value: "Легковой транспорт", label: "Легковой" },
  { value: "Грузовой транспорт", label: "Грузовой" },
  { value: "Спецтехника", label: "Спецтехника" },
  { value: "Сельхозтехника", label: "Сельхозтехника" },
  { value: "Оборудование", label: "Оборудование" },
  { value: "Недвижимость", label: "Недвижимость" },
  { value: "Другое", label: "Другое" },
];
const FILE_EXT = /\.(pdf|jpe?g|png|docx?|xlsx?)$/i;
const FILE_MAX = 10 * 1024 * 1024;

const rub = (value) => Math.round(Number(value) || 0).toLocaleString("ru-RU") + " ₽";

export default {
  name: "ApplicationForm",
  components: { FilePicker },
  props: {
    // own — своя техника, kp — расчёт по КП поставщика
    kind: { type: String, default: "own" },
    // одобренный лимит клиента, ₽; 0 — взять из профиля ЛК (main/getLimit)
    limit: { type: Number, default: 0 },
    // открыто из «Воспользоваться лимитом» — галочка лимита включена сразу
    useLimit: Boolean,
    // форма в окне: после отправки — «Готово» (закрыть окно), иначе — в каталог
    cancelable: Boolean,
    // предзаполнение полей (например, позиция каталога — fromCatalogItem)
    initial: { type: Object, default: () => ({}) },
    // результат быстрого расчёта { payment, total } — показывается в форме и
    // уходит в письмо; наименование техники тогда необязательно
    calc: { type: Object, default: null },
  },
  emits: ["cancel", "sent"],
  data() {
    return {
      types: TYPES,
      // валюта — значение как на сервере, sign — знак в поле стоимости
      currencies: [
        { value: "Рубль", sign: "₽" },
        { value: "Юань", sign: "¥" },
        { value: "Доллар", sign: "$" },
        { value: "Евро", sign: "€" },
      ],
      form: {
        PLType: "",
        PLName: "",
        PLBrand: "",
        PLModel: "",
        PLDate: "",
        PLQty: "1",
        PLPrice: "",
        currency: "Рубль",
        advance: "20",
        // срок лизинга, мес. — границы и значение по умолчанию как в быстром расчёте
        term: "60",
        supplier: "",
        useLimit: this.useLimit,
        name: "",
        phone: "+7",
        email: "",
        comment: "",
        ...this.initial,
      },
      priceText: this.initial.PLPrice
        ? Number(this.initial.PLPrice).toLocaleString("ru-RU")
        : "",
      file: null,
      // свёрнутые части формы: подробности о технике, комментарий, поля контактов
      detailsOpen: false,
      commentOpen: false,
      contactsEdit: false,
      errors: {},
      error: null,
      sending: false,
      success: false,
    };
  },
  computed: {
    nameLabel() {
      if (this.form.PLType === "Недвижимость") return "Объект — офис, склад, помещение";
      if (this.calc) return "Модель, если уже выбрали";
      return "Модель или описание техники";
    },
    priceLabel() {
      return Number(this.form.PLQty) > 1 ? "Стоимость за единицу" : "Стоимость";
    },
    // подсказка вверху формы (без быстрого расчёта): заголовок и пояснение
    hint() {
      if (this.kind === "kp")
        return {
          title: "Достаточно документа",
          text: "Приложите КП поставщика — остальное менеджер уточнит по телефону.",
        };
      return {
        title: "Займёт около минуты",
        text: "Заполните основное — остальное менеджер уточнит по телефону.",
      };
    },
    // контакты из профиля показываются карточкой, пока клиент не нажал «Изменить»
    // и в них нет ошибок
    contactCard() {
      return (
        !this.contactsEdit &&
        !!this.form.name.trim() &&
        this.form.phone.length === 18 &&
        !this.errors.name &&
        !this.errors.phone &&
        !this.errors.email
      );
    },
    initials() {
      return this.form.name
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((part) => part[0])
        .join("")
        .toUpperCase();
    },
    contactLine() {
      return [this.form.phone, this.form.email.trim()].filter((v) => v).join(" · ");
    },
    detailsTitle() {
      return this.form.PLType === "Недвижимость"
        ? "Поставщик или продавец"
        : "Марка, модель, год, поставщик";
    },
    // заполненные подробности — краткой строкой под свёрнутым пунктом
    detailsSummary() {
      const f = this.form;
      const parts =
        f.PLType === "Недвижимость"
          ? [f.supplier]
          : [
              [f.PLBrand, f.PLModel].filter((v) => v).join(" "),
              f.PLDate,
              Number(f.PLQty) > 1 ? f.PLQty + " шт." : "",
              f.supplier ? "поставщик " + f.supplier : "",
            ];
      return parts.filter((v) => v && String(v).trim()).join(" · ");
    },
    ...mapGetters({
      getEmail: "main/getEmail",
      getFio: "main/getFio",
      getPhoneMasked: "main/getPhoneMasked",
    }),
    // ориентир финансирования: стоимость × количество за вычетом аванса
    financing() {
      const price = Number(this.form.PLPrice) || 0;
      const qty = Number(this.form.PLQty) || 1;
      const advance = Number(this.form.advance) || 0;
      return price * qty * (1 - advance / 100);
    },
    // одобренный лимит: переданный явно (главная) либо из профиля ЛК (/main) —
    // переключатель лимита есть в форме на любой странице, если лимит есть
    limitValue() {
      return this.limit > 0 ? this.limit : this.$store.getters["main/getLimit"] || 0;
    },
    limitWarning() {
      return (
        this.form.useLimit &&
        this.form.currency === "Рубль" &&
        this.financing > this.limitValue
      );
    },
    limitNotice() {
      if (!this.form.useLimit) {
        return "Заявка оформляется без лимита.";
      }
      let text =
        "Лимит будет передан в заявку. Условия подтвердит менеджер, лимит сейчас не расходуется.";
      if (this.financing > 0 && this.form.currency === "Рубль") {
        text += " Ориентир финансирования после аванса: " + rub(this.financing) + ".";
        if (this.financing > this.limitValue) {
          text +=
            " Это выше лимита — менеджер предложит увеличить аванс или оформить сделку обычным порядком.";
        }
      }
      return text;
    },
  },
  async created() {
    this.form.email = this.getEmail || "";
    this.fillProfile();
    // профиль мог ещё не загрузиться (вход, смена профиля) — догружаем
    await this.$store.dispatch("main/loadProfile");
    this.fillProfile();
    // лимит, если его не передали явно и он ещё неизвестен (каталог, избранное)
    if (!(this.limit > 0)) this.$store.dispatch("main/loadLimit");
  },
  methods: {
    rub,
    // имя и телефон из профиля ЛК — только в пустые поля, ввод клиента не трогаем
    fillProfile() {
      if (!this.form.name && this.getFio) this.form.name = this.getFio;
      if (this.form.phone.length <= 2 && this.getPhoneMasked)
        this.form.phone = this.getPhoneMasked;
    },
    setType(value) {
      this.form.PLType = value;
      const { PLType: removed, ...rest } = this.errors;
      this.errors = rest;
    },
    onPriceInput(event) {
      const digits = event.target.value.replace(/\D/g, "").slice(0, 12);
      this.form.PLPrice = digits;
      this.priceText = digits ? Number(digits).toLocaleString("ru-RU") : "";
      event.target.value = this.priceText;
    },
    pickFile(file) {
      if (!file) return;
      if (!FILE_EXT.test(file.name) || file.size > FILE_MAX) {
        this.errors = {
          ...this.errors,
          file: "Выберите PDF, изображение, Word или Excel размером до 10 МБ",
        };
        return;
      }
      this.file = file;
      const { file: removed, ...rest } = this.errors;
      this.errors = rest;
    },
    removeFile() {
      this.file = null;
    },
    // быстрая проверка до отправки; окончательная — на сервере
    validate() {
      const f = this.form;
      const errors = {};
      if (!f.PLType) errors.PLType = "Выберите тип";
      if (!f.PLName.trim() && !this.calc)
        errors.PLName =
          f.PLType === "Недвижимость"
            ? "Опишите объект"
            : "Укажите технику: наименование, марку или модель";
      if (f.PLDate && !/^(19|20)\d{2}$/.test(f.PLDate)) errors.PLDate = "Укажите год выпуска";
      if (!(Number(f.PLQty) >= 1)) errors.PLQty = "Укажите количество";
      if (!(Number(f.PLPrice) > 0)) errors.PLPrice = "Укажите стоимость";
      if (f.advance === "" || !(Number(f.advance) >= 0 && Number(f.advance) < 100))
        errors.advance = "Аванс от 0 до 99%";
      if (!(Number(f.term) >= 12 && Number(f.term) <= 60))
        errors.term = "Срок от 12 до 60 мес.";
      if (!f.name.trim()) errors.name = "Укажите имя";
      if (f.phone.length !== 18) errors.phone = "Введите телефон";
      if (f.email && !/^\S+@\S+\.\S+$/.test(f.email)) errors.email = "Проверьте e-mail";
      if (this.kind === "kp" && !this.file && !f.comment.trim())
        errors.file = "Приложите счёт или КП либо опишите предложение в комментарии";
      return errors;
    },
    // ошибки в свёрнутых частях — раскрываем их и прокручиваем к первой ошибке
    showErrors() {
      const e = this.errors;
      if (e.PLDate || e.PLQty) this.detailsOpen = true;
      if (e.name || e.phone || e.email) this.contactsEdit = true;
      this.$nextTick(() => {
        const first = this.$el.querySelector(".app-form__error");
        if (first) first.scrollIntoView({ behavior: "smooth", block: "center" });
      });
    },
    async send() {
      this.error = null;
      const errors = this.validate();
      this.errors = errors;
      if (Object.keys(errors).length) {
        this.error = "Проверьте выделенные поля";
        this.showErrors();
        return;
      }

      const data = {
        ...this.form,
        kind: this.kind,
        calcPayment: this.calc ? this.calc.payment : "",
        calcTotal: this.calc ? this.calc.total : "",
        useLimit: this.limitValue > 0 && this.form.useLimit,
      };

      this.sending = true;
      try {
        // с файлом — multipart через прокси api_v2 (files[] + data), без — обычный JSON
        const response = this.file
          ? await uploadApi("/requestOwn", { files: [this.file], data }, null, () => {})
          : await postApi("/requestOwn", { data });

        if (response && response.status) {
          this.success = true;
          this.$emit("sent");
        } else {
          this.errors = (response && response.errors) || {};
          this.error =
            (response && (response.error || response.msg)) ||
            "Не удалось отправить заявку. Попробуйте позже.";
          this.showErrors();
        }
      } catch (e) {
        this.error = "Не удалось отправить заявку. Попробуйте позже.";
      } finally {
        this.sending = false;
      }
    },
  },
};
</script>

<style>
/* Заявка на лизинг — компактная форма в стиле банковского приложения.
   Поля — floating-поля ЛК (серые, 50px), остальное — классы app- */
/* подсказка вверху: иконка, заголовок, пояснение */
.app-hint {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
  padding: 12px 14px;
  border-radius: 12px;
  background: #eef4f9;
}

.app-hint__icon {
  display: grid;
  flex: none;
  place-items: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #fff;
  color: #004680;
}

.app-hint__text b {
  display: block;
  font-size: 14px;
  line-height: 18px;
  color: #002a4d;
}

.app-hint__text small {
  display: block;
  font-size: 13px;
  line-height: 18px;
  color: #3d5a73;
}

/* из быстрого расчёта: справа — ежемесячный платёж */
.app-hint__text {
  flex: 1;
  min-width: 0;
}

.app-hint__pay {
  flex: none;
  text-align: right;
}

.app-hint__pay b {
  display: block;
  font-size: 22px;
  line-height: 26px;
  color: #004680;
  white-space: nowrap;
}

.app-hint__pay small {
  display: block;
  font-size: 12px;
  line-height: 16px;
  color: #3d5a73;
}

.app-form .form-group {
  margin-bottom: 0;
}

.app-form__block + .app-form__block {
  margin-top: 24px;
}

/* вопрос-заголовок блока */
.app-form__q {
  margin-bottom: 10px;
  font-size: 15px;
  line-height: 20px;
  font-weight: 700;
  color: #111;
}

.app-form__optional {
  margin-left: 4px;
  font-size: 13px;
  font-weight: 400;
  color: #8a8f94;
}

.app-form__field {
  margin-top: 12px;
}

.app-form__error {
  margin-top: 6px;
  color: #dc3545;
  font-size: 13px;
  line-height: 18px;
}

/* тип — чипсы */
.app-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.app-chip {
  height: 36px;
  padding: 0 14px;
  border: 1px solid #dfe5ea;
  border-radius: 18px;
  background: #fff;
  color: #111;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.15s, border-color 0.15s, color 0.15s;
}

.app-chip:hover {
  border-color: #82d0f5;
}

.app-chip.is-active {
  border-color: #004680;
  background: #004680;
  color: #fff;
}

/* условия: стоимость шире, аванс и срок — по четверти */
.app-terms {
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(0, 1fr) minmax(0, 1fr);
  gap: 12px;
}

/* валюта — переключатель внутри поля стоимости */
.app-money {
  position: relative;
}

/* вес выше шаблонного .form-floating > .form-control { padding: 10px },
   иначе сумма заходит под переключатель валюты */
.app-money .form-floating > .form-control {
  padding-right: 64px;
}

.app-money__cur {
  position: absolute;
  top: 50%;
  right: 8px;
  height: 30px;
  padding: 0 22px 0 10px;
  border: 0;
  border-radius: 8px;
  background: #fff
    url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M1 1l4 4 4-4' fill='none' stroke='%23004680' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")
    no-repeat right 8px center;
  color: #004680;
  font-size: 14px;
  font-weight: 700;
  transform: translateY(-50%);
  appearance: none;
  -webkit-appearance: none;
  cursor: pointer;
}

.app-money__cur:focus-visible {
  outline: 2px solid #004680;
  outline-offset: 1px;
}

/* лимит — карточка с переключателем */
.app-limit {
  margin-top: 12px;
  padding: 12px 14px;
  border-radius: 12px;
  background: #e9eff2;
  color: #002a4d;
}

.app-limit_warning {
  background: #fff4d6;
  color: #5c4400;
}

.app-limit__switch {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin: 0 0 6px;
  cursor: pointer;
}

.app-limit__switch small {
  display: block;
  font-size: 13px;
}

.app-limit__notice {
  font-size: 13px;
  line-height: 18px;
}

/* шаблон ЛК прячет все чекбоксы (visibility: hidden; position: absolute) —
   переключатель возвращаем явно */
.app-form .app-switch {
  visibility: visible;
  flex: none;
  position: relative;
  width: 44px;
  height: 24px;
  margin: 0;
  border-radius: 12px;
  background: #b3c8d9;
  appearance: none;
  -webkit-appearance: none;
  cursor: pointer;
  transition: background-color 0.15s;
}

.app-form .app-switch::after {
  content: "";
  position: absolute;
  top: 3px;
  left: 3px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  transition: transform 0.15s;
}

.app-form .app-switch:checked {
  background: #004680;
}

.app-form .app-switch:checked::after {
  transform: translateX(20px);
}

.app-form .app-switch:focus-visible {
  outline: 2px solid #004680;
  outline-offset: 2px;
}

/* контакты из профиля — карточкой */
.app-contact {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 12px;
  background: #e9eff2;
}

.app-contact__avatar {
  display: grid;
  flex: none;
  place-items: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #004680;
  color: #fff;
  font-size: 14px;
  font-weight: 700;
}

.app-contact__info {
  flex: 1;
  min-width: 0;
}

.app-contact__info b,
.app-contact__info small {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* телефон и почта — переносятся, а не обрезаются (на телефоне почта
   уходит на вторую строку) */
.app-contact__info small {
  font-size: 13px;
  color: #5c5c5c;
  white-space: normal;
  overflow-wrap: anywhere;
}

/* текст кнопки — на правом отступе карточки (14px), как суммы в других карточках */
.app-contact__edit {
  flex: none;
  margin-right: -10px;
  padding: 8px 10px;
  border: 0;
  border-radius: 8px;
  background: none;
  color: #004680;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
}

.app-contact__edit:hover {
  background: #fff;
}

/* «Дополнительно» — список свёрнутых пунктов, как в настройках приложения */
.app-extra {
  overflow: hidden;
  border: 1px solid #e3e8ec;
  border-radius: 12px;
  background: #fff;
}

.app-extra__item + .app-extra__item {
  border-top: 1px solid #e3e8ec;
}

.app-extra__head,
.app-drop_slim {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  margin: 0;
  padding: 10px 14px;
  border: 0;
  background: #fff;
  color: #111;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.15s;
}

.app-extra__head:hover,
.app-drop_slim:hover,
.app-drop_slim.is-over {
  background: #f5f8fa;
}

.app-extra__icon,
.app-drop_slim .app-drop__icon,
.app-file_slim .app-file__icon {
  display: grid;
  flex: none;
  place-items: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: #e9eff2;
  color: #004680;
}

.app-extra__text,
.app-drop__text {
  flex: 1;
  min-width: 0;
}

.app-extra__text b,
.app-drop__text b {
  display: block;
  font-size: 14px;
  font-weight: 700;
}

.app-extra__text small,
.app-drop__text small {
  display: block;
  overflow: hidden;
  font-size: 13px;
  color: #5c5c5c;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.app-drop__text u {
  color: #004680;
}

.app-extra__chev {
  flex: none;
  color: #8a8f94;
  transition: transform 0.15s;
}

.app-extra__chev.is-open {
  transform: rotate(180deg);
}

.app-extra__body {
  padding: 4px 14px 14px;
}

/* файл — крупная карточка (расчёт по КП) */
.app-drop:not(.app-drop_slim) {
  display: flex;
  align-items: center;
  gap: 14px;
  margin: 0;
  padding: 16px;
  border: 1.5px dashed #b3c8d9;
  border-radius: 12px;
  cursor: pointer;
  transition: background-color 0.15s, border-color 0.15s;
}

.app-drop:not(.app-drop_slim):hover,
.app-drop:not(.app-drop_slim).is-over {
  border-color: #004680;
  background: #f4f8fb;
}

.app-drop:not(.app-drop_slim) .app-drop__icon {
  display: grid;
  flex: none;
  place-items: center;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: #e9eff2;
  color: #004680;
}

.app-drop:not(.app-drop_slim).has-error {
  border-color: #dc3545;
}

.app-drop__text small {
  white-space: normal;
}

/* выбранный файл */
.app-file {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 10px 10px 14px;
  border-radius: 12px;
  background: #e9eff2;
}

.app-file_slim {
  border-radius: 0;
  background: #fff;
}

.app-file__icon {
  display: grid;
  flex: none;
  place-items: center;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: #fff;
  color: #004680;
}

.app-file__name {
  flex: 1;
  min-width: 0;
}

.app-file__name b {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.app-file__name small {
  font-size: 13px;
  color: #5c5c5c;
}

.app-file__remove {
  display: grid;
  flex: none;
  place-items: center;
  width: 32px;
  height: 32px;
  border: 0;
  border-radius: 50%;
  background: #e9eff2;
  color: #5c5c5c;
  cursor: pointer;
}

.app-file:not(.app-file_slim) .app-file__remove {
  background: #fff;
}

.app-file__remove:hover {
  color: #dc3545;
}

/* одна большая кнопка, закреплена внизу (в окне — во всю ширину окна) */
.app-form__footer {
  position: sticky;
  bottom: 0;
  z-index: 2;
  margin-top: 24px;
  padding-top: 14px;
  background: #fff;
}

/* окно заявки: отступы шапки и тела 20px (16px на телефоне) — у шаблонного
   окна 14px, для формы в стиле приложения тесно.
   Селекторы с .modal-header/.modal-body: стили компонентов подключаются раньше
   template_styles.css (см. main.js), и при равном весе шаблонный
   .modal-body { padding: 1rem } перебил бы эти отступы — тогда низ формы
   с отрицательными полями выходил бы за края окна */
.modal-header.app-modal__head {
  padding: 16px 20px;
}

.modal-body.app-modal__body {
  padding: 20px;
}

/* в окне: на ширину окна; bottom −0.5rem компенсирует padding .modal из
   шаблона ЛК — иначе под закреплённым низом виден просвет с текстом формы */
.app-modal__body .app-form__footer {
  bottom: -0.5rem;
  margin: 24px -20px -20px;
  padding: 14px 20px 20px;
  border-top: 1px solid #eef0f2;
  border-radius: 0 0 8px 8px;
}

.app-form__footer-error {
  margin-bottom: 10px;
  color: #dc3545;
  font-size: 13px;
  text-align: center;
}

.app-form .app-form__submit {
  width: 100%;
  height: 50px;
  font-size: 16px;
}

.app-form__trust {
  margin: 8px 0 0;
  font-size: 12px;
  line-height: 16px;
  color: #8a8f94;
  text-align: center;
}

/* экран «Заявка отправлена» */
.app-done {
  display: grid;
  justify-items: center;
  gap: 10px;
  padding: 24px 0 8px;
  text-align: center;
}

.app-done__icon {
  display: grid;
  place-items: center;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: #f9e547;
  color: #111;
}

.app-done__title {
  font-size: 20px;
  font-weight: 700;
}

.app-done__text {
  max-width: 360px;
  margin: 0 0 8px;
  color: #5c5c5c;
}

@media (max-width: 575px) {
  .modal-header.app-modal__head {
    padding: 14px 16px;
  }

  .modal-body.app-modal__body {
    padding: 16px;
  }

  .app-modal__body .app-form__footer {
    margin: 24px -16px -16px;
    padding: 14px 16px 16px;
  }

  .app-terms {
    grid-template-columns: 1fr 1fr;
  }

  .app-terms__price {
    grid-column: 1 / -1;
  }
}
</style>
